import { keepPreviousData, useQuery } from "@tanstack/react-query";
import { addMonths } from "date-fns";
import type React from "react";
import { useState } from "react";

import { Button } from "@/components/Button/Button";
import type { DateRangePickerValue } from "@/components/DateRangePicker/DateRangePicker";
import { DateRangePicker } from "@/components/DateRangePicker/DateRangePicker";
import { ErrorPage } from "@/components/Error/ErrorPage";
import { FullSizeLoader } from "@/components/FullSizeLoader/FullSizeLoader";
import { Download01Icon } from "@/components/Icons/Download01Icon";
import { DocumentPaper } from "@/components/Paper/DocumentPaper";
import { useBool } from "@/hooks/useBool";
import { useQueryParam } from "@/hooks/useQueryParam";
import { NoData } from "@/modules/analytics/components/NoData";
import { useAnalyticsQueries } from "@/queries/analytics";
import { useTranslation } from "@/translations";

import { FahHistoryQuestionGroupCard } from "./components/FahHistoryQuestionGroupCard";
import { FahStatsCard } from "./components/FahHistoryStatsCard";
import { FeelingAtHomeExportModal } from "./components/FeelAtHomeExportModal";

// Did not have feeling at home data before this.
const MIN_FAH_DATE = new Date("2023-06-01");

export function FeelingAtHomeDetailsPage(): React.ReactNode {
  const { t } = useTranslation();

  const [queryParamMonth] = useQueryParam("month");
  const [dateRange, setDateRange] = useState<DateRangePickerValue | undefined>(() => {
    if (!queryParamMonth) {
      return undefined;
    }

    const date = new Date(queryParamMonth + "-01");
    if (isNaN(date.getTime())) {
      return undefined;
    }

    return [date, addMonths(date, 1)];
  });
  const [showSurveyExportModal, showSurveyExportModalHandlers] = useBool(false);

  const analyticsQueries = useAnalyticsQueries();
  const {
    data: fahHistoryDetails,
    isPending: isPendingFahHistoryDetails,
    isPlaceholderData,
    error: errorFahHistoryDetails,
  } = useQuery({
    ...analyticsQueries.getFahHistoryDetails({
      startDate: dateRange?.[0]?.toISOString(),
      endDate: dateRange?.[1]?.toISOString(),
    }),
    placeholderData: keepPreviousData,
  });

  if (isPendingFahHistoryDetails) {
    return <FullSizeLoader withPadding />;
  }

  if (errorFahHistoryDetails) {
    return <ErrorPage error={errorFahHistoryDetails} />;
  }

  return (
    <DocumentPaper
      title={t("page.fah-history.title")}
      subTitle={t("page.fah-history.subtitle")}
      actions={
        <Button
          styling="secondary"
          icon={<Download01Icon />}
          onClick={showSurveyExportModalHandlers.setTrue}
          disabled={isPlaceholderData}
        >
          {t("page.feeling-at-home-details.export.button")}
        </Button>
      }
    >
      <div className="flex w-full flex-col gap-5">
        <div className="flex max-w-3xl flex-col flex-wrap gap-5 @2xl:flex-row">
          <FahStatsCard
            label={t("page.fah-history.label.date-range")}
            value={
              <div className="max-w-md">
                <DateRangePicker
                  value={dateRange}
                  onChange={(range) => range && setDateRange(range)}
                  placeholder={[t("common.label.start-date"), t("common.label.end-date")]}
                  min={MIN_FAH_DATE}
                  max={new Date()}
                />
              </div>
            }
          />
          <FahStatsCard
            label={t("page.fah-history.label.total-responses")}
            value={fahHistoryDetails.totalResponseCount ?? t("common.label.not-applicable")}
            isLoading={isPlaceholderData}
          />
        </div>
        <div className="grid w-full grid-cols-1 gap-5 @6xl:grid-cols-2">
          {fahHistoryDetails.questions.map((question, index) => (
            <FahHistoryQuestionGroupCard
              key={question.id}
              order={index + 1}
              question={question}
              startDate={dateRange?.[0]}
              endDate={dateRange?.[1]}
              isLoading={isPlaceholderData}
            />
          ))}
          {fahHistoryDetails.questions.length === 0 && (
            <div className="relative flex w-full flex-col justify-between gap-10 rounded-lg bg-white p-4 md:gap-6 md:p-6">
              <NoData horizontal />
            </div>
          )}
        </div>
      </div>
      <FeelingAtHomeExportModal
        isOpened={showSurveyExportModal}
        startDate={dateRange?.[0]}
        endDate={dateRange?.[1]}
        onOpenChange={showSurveyExportModalHandlers.set}
      />
    </DocumentPaper>
  );
}
